import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import {UpdateWork} from 'ApplicationEntry/models/AWDStatus/UpdateWork';
import axios from 'Shared/utilities/axiosInstance';
import {getAxiosConfig, getUrlFromUri, handleResponse, handleImageResponse} from 'Shared/utilities/fetchHelpers';
import {fromLocalStorage, LocalStorageKey} from 'Shared/utilities/localStorage';

import {Application} from 'ApplicationEntry/sagas/saveToLife70Saga';

type PolicyRecordModel = {
  awdKeyId: string;
  priorityIncrease: number;
  fieldValues: {
    name: string;
    value: string;
  }[];
};

type WorkInstance = {
  id: string;
  businessAreaNameContent: string;
  typeNameContent: string;
  statusNameContent: string;
  userId: string;
  priorityIncrease: number;
  fieldValues: {
    name: string;
    value: string;
  }[];
};

const getAWDHeaders = () => {
  const csrf_token = fromLocalStorage(LocalStorageKey.AWDCsrfToken)();
  const dst_session = fromLocalStorage(LocalStorageKey.AWDDSTSession)();

  return {
    csrf_token,
    dst_session,
  };
};

export const parseResponse = (awdLoginResponse: string) => {
  const requestBodyString = awdLoginResponse.slice(awdLoginResponse.indexOf('body:') + 5);
  const trimmedRequestBodyString = requestBodyString.slice(0, requestBodyString.length - 3);

  return JSON.parse(trimmedRequestBodyString);
};

export const getPolicyNumber = async (isElectronic: boolean) => {
  const axiosConfig = getAxiosConfig();
  let queryStringParameters = [];

  if (isElectronic) {
    queryStringParameters.push(`isElectronic=${isElectronic}`);
  }

  let response = await axios.get(
    getUrlFromUri(
      `/all/policynumber` + (queryStringParameters.length > 0 ? `?${queryStringParameters.join('&')}` : '')
    ),
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );
  return handleResponse(response);
};

/// Creative naming was required here due to Sedated's overzealous variable naming policy
export const loginToAWD = async (username: string, thingThatUsersProvideToAuthenticate: string) => {
  const axiosConfig = getAxiosConfig();
  const thingThatUsersUseToAuthenticateHeaderPropertyName = 'awd_' + 'pa' + 'ssword';

  let response = await axios.post(getUrlFromUri('/awd/auth/signon'), undefined, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      awd_username: username,
      [thingThatUsersUseToAuthenticateHeaderPropertyName]: thingThatUsersProvideToAuthenticate,
    },
  });
  return handleResponse(response);
};

export const getWorkItem = async () => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri('/awd/work'), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getAssignedWorkItem = async (page?: number, pageSize?: number) => {
  const axiosConfig = getAxiosConfig();

  let queryStringParameters = [];

  if (page !== undefined && pageSize !== undefined) {
    queryStringParameters.push(`page=${page}&pageSize=${pageSize}`);
  }

  let response = await axios.get(
    getUrlFromUri(`/awd/work/assigned`) +
      (queryStringParameters.length > 0 ? `?${queryStringParameters.join('&')}` : ''),
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );
  return handleResponse(response);
};

export const getWorkItemChildren = async (childId: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/${childId}/children`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getWorkInformation = async (awdWorkKeyid: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/${awdWorkKeyid}/workinfo`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getPolicyRecord = async (awdworkkeyid: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/${awdworkkeyid}/policy`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const createChildrenRelationship = async (awdWorkKeyId: string, childKey: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.put(getUrlFromUri(`/awd/work/${awdWorkKeyId}/children/${childKey}`), undefined, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getPolicyHistory = async (awdworkkeyid: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/${awdworkkeyid}/policy/history`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const lockWorkItem = async (awdworkkeyid: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.put(getUrlFromUri(`/awd/work/${awdworkkeyid}/lock`), undefined, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const updatePolicyRecord = async (policyRecordItems: PolicyRecordModel) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.put(getUrlFromUri('/awd/work'), policyRecordItems, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getNextImage = async (sourceInstanceId: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/${sourceInstanceId}/pdfimage`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
    responseType: 'blob',
  });
  return handleImageResponse(response);
};

export const getOccupation = async (name: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/occupation/${name}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getProductFamiliesByProductType = async (
  applicationType: ApplicationType,
  stateExclusivityIgnored?: string,
  ignoreNonexclusive?: boolean
) => {
  const axiosConfig = getAxiosConfig();

  let queryStringParameters = [];

  if (stateExclusivityIgnored !== undefined) {
    queryStringParameters.push(`stateExclusivityIgnored=${stateExclusivityIgnored}`);
  }

  if (ignoreNonexclusive !== undefined) {
    queryStringParameters.push(`ignoreNonexclusive=${ignoreNonexclusive}`);
  }

  let response = await axios.get(
    getUrlFromUri(
      `/all/product/family/product/type/${applicationType}` +
        (queryStringParameters.length > 0 ? `?${queryStringParameters.join('&')}` : '')
    ),
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );

  return handleResponse(response);
};

export const getProducts = async (
  applicationType: ApplicationType,
  stateExclusivityIgnored?: string,
  ignoreNonexclusive?: boolean
) => {
  const axiosConfig = getAxiosConfig();

  let queryStringParameters = [];

  if (stateExclusivityIgnored !== undefined) {
    queryStringParameters.push(`stateExclusivityIgnored=${stateExclusivityIgnored}`);
  }

  if (ignoreNonexclusive !== undefined) {
    queryStringParameters.push(`ignoreNonexclusive=${ignoreNonexclusive}`);
  }

  let response = await axios.get(
    getUrlFromUri(
      `/all/product/view/product/type/${applicationType}` +
        (queryStringParameters.length > 0 ? `?${queryStringParameters.join('&')}` : '')
    ),
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );
  return handleResponse(response);
};

export const checkPolicyNumber = async (policyNumber: string) => {
  const axiosConfig = getAxiosConfig();
  let response = await axios.get(getUrlFromUri(`/all/policynumber/${policyNumber}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const createPolicyNumber = async (policyNumber: string) => {
  const axiosConfig = getAxiosConfig();
  let response = await axios.post(getUrlFromUri(`/all/policynumber/${policyNumber}`), undefined, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getCaseName = async (caseNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/case/${caseNumber}/name`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getProduct = async (friendlyName: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/product/view/product/friendlyName/${friendlyName}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getForm = async (formNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/form/${formNumber}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getPlanType = async (family: string, product: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/plantype/${family}/${product}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getAgentNameByAgentNumber = async (agentNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/agent/${agentNumber}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getSmokerCodes = async () => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/underwriting/smoking/status`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getBillingModes = async () => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/billing/mode`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getRiders = async (plan: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/product/view/plan/${plan}/rider`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const saveApplicationToLife70 = async (applicationModel: Application) => {
  const axiosConfig = getAxiosConfig();

  const response = await axios.post(getUrlFromUri('/all/enrollment'), applicationModel, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return response;
};

export const updateApplicationToLife70 = async (applicationModel: Application) => {
  const axiosConfig = getAxiosConfig();

  const response = await axios.put(getUrlFromUri('/all/enrollment'), applicationModel, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return response;
};

export const getBusinessArea = async () => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd​/user​/businessareas`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const validateAppAgainstAUE70 = async (applicationModel: Application) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.post(getUrlFromUri('/all/enrollment/validate'), applicationModel, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getWorkTypes = async (businessArea: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/type/${businessArea}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const geStatusListApi = async (businessArea: string, workType: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/type/${businessArea}/status/${workType}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const getUnderwriters = async () => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri('/all/underwriting/underwriter'), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const createWorkItem = async (workItem: WorkInstance) => {
  const axiosConfig = getAxiosConfig();

  const response = await axios.post(getUrlFromUri('/awd/work'), workItem, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const updateWorkItem = async (updateWork: UpdateWork) => {
  const axiosConfig = getAxiosConfig();

  const response = await axios.put(getUrlFromUri('/awd/work'), updateWork, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return response.status;
};

export const getUnderwritingQuestions = async (formNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/underwriting/question/${formNumber}/document`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getGroupProduct = async (caseNumber: string, planCode: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/product/view/product/group/${caseNumber}/${planCode}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getEnrollmentApplication = async (policyNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/enrollment/${policyNumber}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });
  return handleResponse(response);
};

export const issuePolicy = async (policyNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.post(
    getUrlFromUri(`/all/enrollment/policy`),
    {policyNumber},
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );

  return response;
};

export const policyStatus = async (policyNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/enrollment/policy/${policyNumber}/status`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const isAllstateAgent = async (agentNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/agent/${agentNumber}/allstate`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getBusinessAreas = async () => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/type/businessarea`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return response;
};

export const getComments = async (awdWorkKeyId: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/${awdWorkKeyId}/comment`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getCommentById = async (awdWorkKeyId: string, commentId: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/awd/work/${awdWorkKeyId}/comment/${commentId}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const addComment = async (comment: string, awdWorkKeyId: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.post(
    getUrlFromUri(`/awd/work/${awdWorkKeyId}/comment`),
    {comment},
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );

  return handleResponse(response);
};

export const getTakeover = async (caseNumber: string, product: string, fullSsn: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.post(
    getUrlFromUri(`/all/case/takeover/lookup`),
    { caseNumber, product, fullSsn },
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );

  return handleResponse(response);
};

export const createTakeover = async (takeoverSsnId: string, saveToLife70?: boolean, saveToAwd?: boolean) => {
  const axiosConfig = getAxiosConfig();

  let queryStringParameters = [];

  if (saveToLife70) {
    queryStringParameters.push(`saveToLife70=${saveToLife70}`);
  }

  if (saveToAwd) {
    queryStringParameters.push(`saveToAwd=${saveToAwd}`);
  }

  let response = await axios.post(
    getUrlFromUri(
      `/all/case/takeover` + (queryStringParameters.length > 0 ? `?${queryStringParameters.join('&')}` : '')
    ),
    {takeoverSsnId},
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );

  return handleResponse(response);
};

export const updateTakeover = async (takeoverSsnId: string, saveToLife70?: boolean, saveToAwd?: boolean) => {
  const axiosConfig = getAxiosConfig();

  let queryStringParameters = [];

  if (saveToLife70) {
    queryStringParameters.push(`saveToLife70=${saveToLife70}`);
  }

  if (saveToAwd) {
    queryStringParameters.push(`saveToAwd=${saveToAwd}`);
  }

  let response = await axios.put(
    getUrlFromUri(
      `/all/case/takeover` + (queryStringParameters.length > 0 ? `?${queryStringParameters.join('&')}` : '')
    ),
    {takeoverSsnId},
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );

  return handleResponse(response);
};

export const getSalaryDeductionOccupationClassInfo = async (caseNumber: string, life70plan: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/occupation/class/sald/${caseNumber}/${life70plan}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getSaldOccClassInfo = async (
  saldOccClass: string,
  isMentalNervousExclusion: string,
  situsState: string
) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(
    getUrlFromUri(`/all/occupation/${saldOccClass}/${isMentalNervousExclusion}/${situsState}`),
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );

  return handleResponse(response);
};

export const getWriteToApplicantStatus = async (caseNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/case/${caseNumber}/applicant/status`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getSalaryDeductionBillingInfo = async (caseNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/billing/sald/${caseNumber}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getPACData = async (ssn: string, firstName: string, lastName: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.post(
    getUrlFromUri(`/all/ach/pacData`),
    { ssn, firstName, lastName },
    {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        ...getAWDHeaders(),
      },
    }
  );

  return handleResponse(response);
};

export const getACHIndicator = async (caseNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/case/${caseNumber}/achIndicator`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};

export const getManagedStatus = async (caseNumber: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/case/getManagedStatus/${caseNumber}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    }
  });

  return handleResponse(response);
}

export const getUserAuthorizedInfo = async (userName: string) => {
  const axiosConfig = getAxiosConfig();

  let response = await axios.get(getUrlFromUri(`/all/enrollment/policy/express/auth/${userName}`), {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      ...getAWDHeaders(),
    },
  });

  return handleResponse(response);
};
